const { fetchJSON, getDefaultRequestConfig, getElement } = window.PURELEI.Utils

const defaultSectionsUrl = () => {
  const appProxyUrls = [`/apps/`, `/a/`, `/tools/`, `/community/`]

  if (appProxyUrls.some((url) => window.location.pathname.includes(url))) {
    return `/`
  }

  return window.location.pathname
}

/** @const {string} The context in which we want the sections to be rendered in */
const DEFAULT_SECTIONS_URL = defaultSectionsUrl()

/**
 * @typedef {Object} PotentialCartItem
 *
 * @property {number} id The variant's unique ID
 * @property {number} quantity The quantity of items to be added to the cart
 * @property {Object} [properties] Line item property key/values
 */

/**
 * @typedef {Object} PureleiCartSectionOptions
 *
 * @property {string[]} [sections] - The sections we want rendered alongside any cart
 *   changes
 * @property {string} [sectionsUrl] - The context in which we want the sections to be
 *   rendered in in
 */

/**
 * @typedef {PureleiCartSectionOptions} PureleiCartSectionOptionsWithAttributes
 *
 * @property {Object} [attributes] Key/values that we want to add to the cart attributes
 */

/**
 * @typedef {PureleiCartSectionOptions} PureleiCartSectionOptionsWithForm
 *
 * @property {HTMLFormElement} [form] The form that has added the item
 */

/**
 * By Association Only's cart helper methods
 */
class PureleiCart {
  /**
   * @param {HTMLFormElement} form
   * @param {PureleiCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromFormWithoutEngraving(
      form,
      {
        sections = window.PURELEI.sectionRenderer.pushCartSections.map(
            (s) => s.section,
        ),
        sectionsUrl = DEFAULT_SECTIONS_URL,
      } = {},
  ) {
    const formData = new FormData(form)

    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    Array
        .from(formData.entries())
        .filter(([input, value]) =>
            String(input).toLowerCase().includes('engraving') ||
            String(value).toLowerCase().includes('engraving') ||
            String(input).includes('items')
        ).forEach(([input, value]) => formData.delete(input))

    if(formData.get('_selling_plan')) {
      formData.append('selling_plan', formData.get('_selling_plan'))
      formData.delete('_selling_plan')
    }

    return await this.commitCartFormData(form, formData);
  }

  async commitFormData(formData) {
    formData.set('sections', 'push-cart-items,push-cart-footer,push-cart-count,push-cart-recommendations,push-cart-messages')

    window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      formData,
    });

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)
      if (window.dataLayer && state.variant_id) {
        window.dataLayer.push({
          product_title: state.title,
          product_vendor: state.vendor,
          product_original_price: state.original_price / 100,
          product_discount_price: state.final_price / 100,
          product_discount_amount: state.discounted_price / 100,
          product_variant: state.variant_title,
          product_id: state.is,
          product_variant_id: state.variant_id,
          product_sku: state.sku,
        })
      }

      window.PURELEI.dispatchEvent(
          window.PURELEI.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
          {
            formData,
            state,
          },
      )

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }

  }
  async commitCartFormData(form, formData) {
    window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM, {
      form,
      formData,
    });

    const config = getDefaultRequestConfig('javascript')
    config.headers['X-Requested-With'] = 'XMLHttpRequest'
    delete config.headers['Content-Type']
    config.body = formData

    const uuid = window.PURELEI.UUID.v4()

    const uuidPropertyValues = [...formData.entries()].filter(([key]) =>
        key.includes('properties[_link_id'),
    )

    uuidPropertyValues.forEach((property) => {
      formData.set(property[0], uuid)
    })

    try {
      const state = await fetchJSON(`${window.routes.cartAddUrl}`, config)

      if (window.dataLayer && state.variant_id) {
        window.dataLayer.push({
          product_title: state.title,
          product_vendor: state.vendor,
          product_original_price: state.original_price / 100,
          product_discount_price: state.final_price / 100,
          product_discount_amount: state.discounted_price / 100,
          product_variant: state.variant_title,
          product_id: state.is,
          product_variant_id: state.variant_id,
          product_sku: state.sku,
        })
      }

      window.PURELEI.dispatchEvent(
          window.PURELEI.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
          {
            form,
            formData,
            state,
          },
      )

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {PureleiCartSectionOptionsWithAttributes} options Optional values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItemFromForm (
    form,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const formData = new FormData(form)
    formData.append('sections', sections)
    formData.append('sections_url', sectionsUrl)

    return await this.commitCartFormData(form, formData);
  }

  /**
   * @param {PotentialCartItem[]|PotentialCartItem} items
   * @param {PureleiCartSectionOptionsWithAttributes & PureleiCartSectionOptionsWithForm} options Optional
   *   values to pass to /cart/add.js
   *
   * @returns {Promise<Object>}
   */
  async addItem (
    items,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
      attributes = {},
      form,
    } = {},
  ) {
    if (!Array.isArray(items)) {
      items = [items]
    }

    window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.BEFORE_ADD, {
      items,
      attributes,
      form,
    })

    const body = {
      items,
      attributes,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartAdd(body)

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.AFTER_ADD, {
        items,
        state,
        form,
      })

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      this.handleAddToCartError(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The amount of the item we want change the line item to. Can be 0.
   * @param {PureleiCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateQuantity (
    key,
    quantity,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.PURELEI.dispatchEvent(
      window.PURELEI.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
      {
        key,
        quantity,
      },
    )
    const body = {
      id: key,
      quantity,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.PURELEI.dispatchEvent(
        window.PURELEI.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
        {
          state,
        },
      )

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {PureleiCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItem (
    key,
    kiteFreeGift,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.PURELEI.dispatchEvent(
      window.PURELEI.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { key },
    )
    const body = {
      id: key,
      quantity: 0,
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartChange(body)

      window.PURELEI.dispatchEvent(
        window.PURELEI.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
          kiteFreeGift,
        },
      )

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string[]} keys The key of the line item we want to change
   * @param {PureleiCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async removeItems (
    keys,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.PURELEI.dispatchEvent(
      window.PURELEI.EVENTS.PUSH_CART.BEFORE_UPDATE_REMOVE_ITEM,
      { keys },
    )

    const body = {
      updates: Object.fromEntries(keys.map((k) => [k, 0])),
    }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.PURELEI.dispatchEvent(
        window.PURELEI.EVENTS.PUSH_CART.AFTER_UPDATE_REMOVE_ITEM,
        {
          state,
        },
      )
      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * @param {string} key The key of the line item we want to change
   * @param {number} quantity The quantity of items to be added to the cart. If this isn't set then Shopify will reset it back to 1
   * @param {object} properties An object of key/value pairs you want to update
   * @param {PureleiCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateItemProperties (
    key,
    quantity,
    properties,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    const body = { id: key, properties, quantity }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      return this.cartChange(body)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {object} attributes An object of key/value pairs you want to update
   * @param {PureleiCartSectionOptions} options
   *
   * @returns {Promise<Object>}
   */
  async updateCartAttributes (
    attributes,
    {
      sections = window.PURELEI.sectionRenderer.pushCartSections.map(
        (s) => s.section,
      ),
      sectionsUrl = DEFAULT_SECTIONS_URL,
    } = {},
  ) {
    window.PURELEI.dispatchEvent(
      window.PURELEI.EVENTS.PUSH_CART.BEFORE_UPDATE_CART_ATTRIBUTES,
      {
        attributes,
      },
    )

    const body = { attributes }

    if (sections.length) {
      body.sections = sections
      body.sections_url = sectionsUrl
    }

    try {
      const state = await this.cartUpdate(body)

      window.PURELEI.dispatchEvent(
        window.PURELEI.EVENTS.PUSH_CART.AFTER_UPDATE_CART_ATTRIBUTES,
        {
          attributes,
        },
      )

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.PUSH_CART.CHANGED, {
        state,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {string} note The note we want to set
   *
   * @returns {Promise<Object>}
   */
  async updateNote (note) {
    window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.CART_NOTE.BEFORE_CHANGE, {
      note,
    })

    try {
      const state = await this.cartUpdate({ note })

      window.PURELEI.dispatchEvent(window.PURELEI.EVENTS.CART_NOTE.AFTER_CHANGE, {
        state,
        note,
      })

      return state
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  handleAddToCartError (res) {
    /**
     * @type {PushCart}
     */
    const pushCart = getElement('push-cart')
    if (!pushCart) return

    pushCart.handleError(res)
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartAdd (body) {
    return fetchJSON(`${window.routes.cartAddUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartChange (body) {
    return fetchJSON(`${window.routes.cartChangeUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }

  /**
   * @param {object} body An object of key/value pairs you want to update
   *
   * @returns {Promise<Object>}
   */
  cartUpdate (body) {
    return fetchJSON(`${window.routes.cartUpdateUrl}`, {
      ...getDefaultRequestConfig(),
      ...{ body: JSON.stringify(body) },
    })
  }
}

window.PURELEI.Cart = new PureleiCart()
